import { SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE } from '@eventbrite/social-login-facebook';
import { ISocialLogInApiResponseProps, socialLogIn } from '../api/social';
import { GA_APPLE, TOSMODAL_SOCIAL_SHOWED } from '../constants/analytics';
import {
    EXTERNAL_UNKNOWN_ERROR,
    LOOKUP_SOCIAL_USER_EXISTENT,
    LOOKUP_SOCIAL_USER_EXISTENT_IN_FACEBOOK_SOFT_ACCOUNT,
    LOOKUP_SOCIAL_USER_EXISTENT_IN_GOOGLE_SOFT_ACCOUNT,
    LOOKUP_SOCIAL_USER_EXISTENT_IN_SOFT_ACCOUNT,
} from '../constants/apiErrors';
import { IAllContextsProps } from '../constants/interfaces';
import { UNKNOWN_EXTERNAL_PROVIDER_ERROR_MESSAGE } from '../constants/messages';
import { trackAppleLoginSuccess } from '../tracking/apple';
import {
    trackHandleOnErrorMetric,
    trackLoginSuccessful,
} from '../tracking/login';
import { trackAccountAutolinked } from '../tracking/social';
import { lookUpUserAction } from './lookup';
import { handleLoginSuccess, ISocialLogInUserActionDataProps } from './social';
import { handleSocialLogInErrors } from './socialErrors';
import { handleShowToS } from './tos';

export const handleAppleLoginAttempt = ({
    data,
    socialProvider = '',
    contexts,
}: {
    data: ISocialLogInUserActionDataProps;
    socialProvider?: string;
    contexts: IAllContextsProps;
}) => {
    const _data = getSocialLoginApiData({
        socialProvider,
        data,
    });
    const { email, sub } = data;

    contexts.userContext?.methods.setSocialData({
        [socialProvider]: {
            ..._data,
            sourceProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
        },
    });

    // Check this catch as it may not be triggering due to this action never failing.
    lookUpUserAction({
        data: { email: email, sub },
        contexts,
        socialProvider,
    }).catch(({ message } = {}) => {
        if (message === LOOKUP_SOCIAL_USER_EXISTENT_IN_SOFT_ACCOUNT) {
            // Here we need to display ToS Modal
            contexts.userContext?.methods.setTosSocialStatus(message);
            handleShowToS({
                action: TOSMODAL_SOCIAL_SHOWED,
                contexts,
            });
        } else {
            if (
                message === LOOKUP_SOCIAL_USER_EXISTENT ||
                message ===
                    LOOKUP_SOCIAL_USER_EXISTENT_IN_FACEBOOK_SOFT_ACCOUNT ||
                message === LOOKUP_SOCIAL_USER_EXISTENT_IN_GOOGLE_SOFT_ACCOUNT
            ) {
                return handleAppleSocialLogin({
                    data: {
                        accessToken: _data.accessToken ?? '',
                        sourceProvider: _data.sourceProvider,
                        email: _data.email ?? '',
                    },
                    isSoftAccount:
                        message === LOOKUP_SOCIAL_USER_EXISTENT_IN_SOFT_ACCOUNT,
                    contexts,
                });
            } else if (!message) {
                trackHandleOnErrorMetric({
                    errorMessage: EXTERNAL_UNKNOWN_ERROR,
                    label: socialProvider,
                    analytics: contexts.configContext?.analytics,
                });

                contexts.notificationsContext?.methods.setGeneralNotification({
                    message: UNKNOWN_EXTERNAL_PROVIDER_ERROR_MESSAGE,
                });

                return Promise.resolve();
            }
        }
    });
};

export const handleAppleSocialLogin = ({
    data,
    isSoftAccount = false,
    contexts,
}: {
    data: {
        accessToken: string;
        sourceProvider: string;
        email: string;
    };
    isSoftAccount?: boolean;
    contexts: IAllContextsProps;
}) => {
    contexts.configContext?.socialMethods?.apple?.login?.attempt?.();

    return socialLogIn(data)
        .then((response: ISocialLogInApiResponseProps) => {
            const analytics = contexts.configContext?.analytics;

            contexts.configContext?.socialMethods?.apple?.login?.success?.(
                response,
            );

            if (response.isAccountAutolinked) {
                const extraInfo = isSoftAccount
                    ? '_SoftAccount'
                    : '_HardAccount';

                trackAccountAutolinked({
                    socialProvider: GA_APPLE,
                    extraInfo,
                    analytics,
                });

                trackLoginSuccessful({
                    socialProvider: GA_APPLE,
                    analytics,
                });
            } else {
                trackAppleLoginSuccess({ response, analytics });
            }

            return handleAppleLoginSuccess({
                data,
                response,
                contexts,
            });
        })
        .catch((error: any) => {
            contexts.configContext?.socialMethods?.apple?.login?.error?.(error);

            handleSocialLogInErrors({
                _errorSpec: error,
                socialProvider: data.sourceProvider,
                responseErrorObj: data,
                contexts,
            });
        });
};

export const handleAppleLoginSuccess = ({
    response,
    data,
    contexts,
}: {
    response: ISocialLogInApiResponseProps;
    data: {
        accessToken: string;
        sourceProvider: string;
        email: string;
    };
    contexts: IAllContextsProps;
}) => {
    // Fresh account coming from the social provider
    // Scenario: appleFreshAccount, appleFreshAccount_AddingExistingAccount, appleFreshAccount_AddingSoftAccount
    contexts.userContext?.methods.lookup.setExistentAccount(data.email);
    // Already done in: handleAppleLoginAttempt ?
    contexts.userContext?.methods.setSocialData({
        [SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE]: {
            ...data,
            sourceProvider: SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE,
        },
    });

    handleLoginSuccess({ response, contexts });
};

/**
 * Returns a useful data structure for the socialLogin api
 */
export const getSocialLoginApiData = ({
    socialProvider,
    data = {},
}: {
    socialProvider: string;
    data?: ISocialLogInUserActionDataProps | {};
}) => {
    const _data = {
        ...data,
        sourceProvider: socialProvider,
    };

    return _data;
};
